import { useTranslation } from 'react-i18next'

import { logger } from '@/lib/logger'

type TranslationResources = Record<string, Record<string, any>>

export type TranslationModule = {
  /**
   * Unique namespace for this translation module.
   *
   * ⚠️ IMPORTANT: To avoid namespace collisions, use the full path relative to src/.
   *
   * Examples:
   * - 'layouts/app-layout/nav-main'
   * - 'pages/login/components/credentials-form'
   * - 'components/auth/two-factor-auth'
   *
   * DO NOT use simple names like 'navMain' or 'button' as they can collide!
   */
  namespace: string
  translations: TranslationResources
}

/**
 * Hook for component-level translations with automatic namespace and multi-language support.
 *
 * ⚠️ NAMESPACE COLLISION WARNING:
 * Each component MUST have a unique namespace. To prevent collisions, use `defineTranslations()`
 * with import.meta.url which automatically generates namespaces from file paths.
 *
 * @example
 * // RECOMMENDED: Automatic namespace generation
 * // In your component's locales/index.ts:
 * import { defineTranslations } from '@/hooks/use-component-translation'
 *
 * import enUs from './enUs.json'
 * import ptBr from './ptBr.json'
 * import esEs from './esEs.json'
 *
 * export default defineTranslations(import.meta.url, {
 *   enUs,
 *   ptBr,
 *   esEs,
 * })
 *
 * // In your component:
 * import locales from './locales'
 *
 * function NavMain() {
 *   const { t } = useComponentTranslation(locales)
 *   return <div>{t('key')}</div>
 * }
 *
 * @example
 * // ALTERNATIVE: Manual namespace (not recommended unless necessary)
 * import { createTranslationModule } from '@/hooks/use-component-translation'
 *
 * export default createTranslationModule({
 *   path: 'layouts/app-layout/nav-main',
 *   translations: { enUs, ptBr, esEs }
 * })
 */
export function useComponentTranslation(module: TranslationModule) {
  const { namespace } = module

  // Validate namespace format to prevent common mistakes. Resource bundles
  // are registered eagerly inside `defineTranslations` / `createTranslationModule`
  // at import time — so by the time we get here the namespace is already
  // wired up and useTranslation can resolve keys without us touching i18n.
  if (process.env.NODE_ENV === 'development') {
    if (!namespace || namespace.trim() === '') {
      logger.error(
        'Empty namespace detected! This will cause issues.',
        { namespace },
        'i18n',
      )
    }

    // Warn if namespace looks too simple (potential collision risk)
    if (namespace && !namespace.includes('/') && namespace.length < 15) {
      logger.warning(
        `Namespace "${namespace}" might be too simple and could collide. ` +
          `Consider using a full path like "layouts/app-layout/${namespace}" to ensure uniqueness.`,
        { namespace },
        'i18n',
      )
    }
  }

  return useTranslation(namespace)
}
